import {
  Boolean,
  Dictionary,
  Literal,
  Null,
  Number,
  Optional,
  Partial,
  Array as RArray,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { ChartSpec } from "./chart/types.js";
import {
  RelativeDateOperationLiteral,
  RelativeDateUnitLiteral,
} from "./datetimeType.js";
import {
  BinaryColumnPredicateOpLiteral,
  ListBinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
} from "./display-table/columnPredicateTypes.js";
import { DisplayTableOutput } from "./display-table/outputTypes.js";
import { CellType, MagicTypeAheadProviderLiteral } from "./enums.js";
import { assertNever } from "./errors.js";
import { HqlAggregationFunctionLiteral } from "./hql/types.js";
import {
  CellId,
  DataConnectionId,
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  MagicApiKeyId,
  MagicEventId,
} from "./idTypeBrands.js";
import { AnthropicModelParams } from "./magic/anthropicTypes.js";
import { OpenAiModelParams } from "./magic/openAiTypes.js";
import { SnowflakeCortexModelParams } from "./magic/snowflakeCortexTypes.js";
import { getNormalEnum } from "./runtypeEnums";
import { VariableName } from "./typeBrands.js";

// The date that Magic Terms of Service became part of the Hex Service Agreement
// All orgs created after this date have auto accepted the Magic TOS since its part of the default Hex Service Agreement
export const MAGIC_TERMS_PART_OF_SERVICE_AGREEMENT_DATE = new Date(
  "2025-02-26T00:00:00.000Z",
);
export function wasOrgCreatedAfterMagicTermsPartOfServiceAgreementDate(
  orgCreatedDateString: Date | string,
): boolean {
  return (
    new Date(orgCreatedDateString) > MAGIC_TERMS_PART_OF_SERVICE_AGREEMENT_DATE
  );
}

export const MagicSupportedImageMimeTypes = Union(
  Literal("image/png"),
  Literal("image/jpeg"),
);
export type MagicSupportedImageMimeTypes = Static<
  typeof MagicSupportedImageMimeTypes
>;

export const SupportedExploreIntentLiteral = Union(
  Literal("SUPPORTED_EXPLORATION"),
);
export const UnsupportedIntentLiteral = Union(
  Literal("UNSUPPORTED_EXPLORATION"),
  Literal("QUESTION"),
  Literal("VISUAL_DETAIL_EDIT"),
  Literal("UNSUPPORTED_VISUALIZATION"),
);
export const ExploreIntentLiteral = Union(
  ...SupportedExploreIntentLiteral.alternatives,
  ...UnsupportedIntentLiteral.alternatives,
);
export type ExploreIntent = Static<typeof ExploreIntentLiteral>;
export const ExploreIntent = getNormalEnum(ExploreIntentLiteral);

export const EXPLORE_INTENT_MESSAGE_MAP: Record<ExploreIntent, string | null> =
  {
    [ExploreIntent.SUPPORTED_EXPLORATION]: null,
    [ExploreIntent.VISUAL_DETAIL_EDIT]:
      "Sorry, we can't change the chart styling with AI yet.",
    [ExploreIntent.UNSUPPORTED_EXPLORATION]:
      "The data required to answer this question is unavailable or unsupported.",
    [ExploreIntent.QUESTION]:
      "Sorry, we can't answer general questions with AI yet.",
    [ExploreIntent.UNSUPPORTED_VISUALIZATION]:
      "That visualization type isn't supported yet.",
  };

export const PromptTemplateTypeLiteral = Union(
  Literal("APPEND_MARKDOWN"),
  Literal("APPEND_PYTHON"),
  Literal("APPEND_R"),
  Literal("COMPLETE_MARKDOWN"),
  Literal("COMPLETE_PYTHON"),
  Literal("COMPLETE_R"),
  Literal("COMPLETE_SQL"),
  Literal("DOCUMENT_PYTHON"),
  Literal("DOCUMENT_R"),
  Literal("DOCUMENT_SQL"),
  Literal("EDIT_EXPLORE"),
  Literal("EDIT_EXPLORE_SEMANTIC"),
  Literal("EDIT_MARKDOWN"),
  Literal("EDIT_PYTHON_DIFF"),
  Literal("EDIT_PYTHON_ADIFF"),
  Literal("EDIT_R"),
  Literal("EDIT_SQL_DIFF"),
  Literal("EDIT_SQL_ADIFF"),
  Literal("EXPERIMENT"),
  Literal("GENERATE_EXPLORE"),
  Literal("GENERATE_EXPLORE_SEMANTIC"),
  Literal("SUMMARIZE_EXPLORE"),
  Literal("FIX_MARKDOWN"),
  Literal("FIX_PYTHON_BASIC"),
  Literal("FIX_PYTHON_DIFF"),
  Literal("FIX_PYTHON_ADIFF"),
  Literal("FIX_R"),
  Literal("FIX_SQL_DIFF"),
  Literal("FIX_SQL_ADIFF"),
  Literal("FIX_SQL_BASIC"),
  Literal("GENERATE_MARKDOWN"),
  Literal("GENERATE_PYTHON"),
  Literal("GENERATE_R"),
  Literal("GENERATE_SQL"),
  Literal("GENERATE_EXAMPLE_PROMPTS"),
  Literal("NAME_SQL_CELL"),
  Literal("NAME_PYTHON_CELL"),
  Literal("NAME_R_CELL"),
  Literal("NAME_MARKDOWN_CELL"),
  Literal("NAME_SQL_CELL_OUTPUT"),
  Literal("NAME_CHART_CELL_OUTPUT"),
  Literal("GENERATE_PROJECT_DESCRIPTION"),
  Literal("GENERATE_PROJECT_HYDE"),
  Literal("NAME_PROJECT"),
  Literal("NAME_PROJECT_FROM_PROMPT"),
  Literal("INFER_MODE_AND_CHAIN_TEMPLATE"),
  Literal("INFER_MODE_AND_CHAIN_TEMPLATE_NO_DATAFRAMES"),
  Literal("GENERATE_CHART"),
  Literal("SIMPLE_CHART"),
  Literal("EDIT_CHART"),
  Literal("NAME_CHART_CELL"),
  Literal("PLAN_CHAIN_CODE"),
  Literal("PLAN_CHAIN_DYNAMIC"),
  Literal("PLAN_CHAIN_CHART"),
  Literal("PLAN_CHAIN_SQL"),
  Literal("SUMMARIZE_CHAIN"),
  Literal("SUMMARIZE_CELL"),
  Literal("INFER_EXPLORE_INTENT"),
  Literal("GENERATE_EXPLORE_PLANNER_STEP"),
  Literal("GENERATE_EXPLORE_EXECUTOR_STEP"),
  Literal("GENERATE_EXPLORE_PLANNER_STEP_SEMANTIC"),
  Literal("GENERATE_EXPLORE_EXECUTOR_STEP_SEMANTIC"),
  Literal("EDIT_EXPLORE_PLANNER_STEP"),
  Literal("EDIT_EXPLORE_EXECUTOR_STEP"),
  Literal("EDIT_EXPLORE_PLANNER_STEP_SEMANTIC"),
  Literal("EDIT_EXPLORE_EXECUTOR_STEP_SEMANTIC"),
);

/**
 * @deprecated Use PromptTemplateV2 instead, with the modelFeatureKey system
 * rather than the old primaryAgentConfigOverride system. If in doubt, ask
 * @johnwiseheart for help.
 */
export type PromptTemplateType = Static<typeof PromptTemplateTypeLiteral>;
export const PromptTemplateType = getNormalEnum(PromptTemplateTypeLiteral);

export const GenerateExploreTemplateLiteral = Union(
  Literal("GENERATE_EXPLORE"),
  Literal("GENERATE_EXPLORE_SEMANTIC"),
  Literal("GENERATE_EXPLORE_PLANNER_STEP"),
  Literal("GENERATE_EXPLORE_EXECUTOR_STEP"),
  Literal("GENERATE_EXPLORE_PLANNER_STEP_SEMANTIC"),
  Literal("GENERATE_EXPLORE_EXECUTOR_STEP_SEMANTIC"),
);
export type GenerateExploreTemplate = Static<
  typeof GenerateExploreTemplateLiteral
>;
export const GenerateExploreTemplate = getNormalEnum(
  GenerateExploreTemplateLiteral,
);

export const isGeneratePlanExecutorPrompt = (
  prompt: PromptTemplateType,
  type: "PLANNER" | "EXECUTOR",
): boolean => {
  switch (type) {
    case "PLANNER":
      return (
        prompt === PromptTemplateType.GENERATE_EXPLORE_PLANNER_STEP ||
        prompt === PromptTemplateType.GENERATE_EXPLORE_PLANNER_STEP_SEMANTIC
      );
    case "EXECUTOR":
      return (
        prompt === PromptTemplateType.GENERATE_EXPLORE_EXECUTOR_STEP ||
        prompt === PromptTemplateType.GENERATE_EXPLORE_EXECUTOR_STEP_SEMANTIC
      );
    default:
      assertNever(type, type);
  }
};

export const MentionedDataframeName = String.withBrand(
  "MentionedDataframeName",
);
export type MentionedDataframeName = Static<typeof MentionedDataframeName>;

export const MagicCellPayload = Union(ChartSpec);
export type MagicCellPayload = Static<typeof MagicCellPayload>;

export const MagicCellChainTemplateTypeWithoutChartOnlyLiteral = Union(
  Literal("CODE"),
  Literal("SQL"),
);
export const MagicCellChainTemplateTypeLiteral = Union(
  ...MagicCellChainTemplateTypeWithoutChartOnlyLiteral.alternatives,
  Literal("CHART"),
);
export type MagicCellChainTemplateTypeLiteralWithoutChartOnly = Static<
  typeof MagicCellChainTemplateTypeWithoutChartOnlyLiteral
>;
export const MagicCellChainTemplateTypeWithoutChartOnly = getNormalEnum(
  MagicCellChainTemplateTypeWithoutChartOnlyLiteral,
);

export type MagicCellChainTemplateType = Static<
  typeof MagicCellChainTemplateTypeLiteral
>;
export const MagicCellChainTemplateType = getNormalEnum(
  MagicCellChainTemplateTypeLiteral,
);

export const MagicRequestModeLiteral = Union(
  Literal("GENERATE"),
  Literal("EDIT"),
  Literal("MORE_CELLS"),
  Literal("WRITEBACK"),
  Literal("MARKDOWN"),
  Literal("INPUT"),
  Literal("DATA_READ"),
  Literal("PREV_CELL_CODE"),
  Literal("QUESTION"),
);
export type MagicRequestMode = Static<typeof MagicRequestModeLiteral>;
export const MagicRequestMode = getNormalEnum(MagicRequestModeLiteral);

// Magic cell types that the LLM agent is aware of
export const AgentMagicCellTypeLiteral = Union(
  Literal(CellType.CODE),
  Literal(CellType.MARKDOWN),
  Literal(CellType.SQL),
  Literal(CellType.CHART),
  Literal(CellType.EXPLORE),
);

export const AgentMagicCellType = getNormalEnum(AgentMagicCellTypeLiteral);
export type AgentMagicCellType = Static<typeof AgentMagicCellTypeLiteral>;

// Magic cell types that only Hex knows about
export const HexMagicCellTypeLiteral = Union(
  Literal(CellType.TEXT),
  Literal(CellType.METRIC),
  Literal(CellType.EXPLORE),
);
export const HexMagicCellType = getNormalEnum(HexMagicCellTypeLiteral);
export type HexMagicCellType = Static<typeof HexMagicCellTypeLiteral>;

export const MagicCellTypeLiteral = Union(
  ...AgentMagicCellTypeLiteral.alternatives,

  ...HexMagicCellTypeLiteral.alternatives,
);
export const MagicCellType = getNormalEnum(MagicCellTypeLiteral);
export type MagicCellType = Static<typeof MagicCellTypeLiteral>;

export const MagicNonStreamingCellType = Union(Literal(MagicCellType.CHART));
export type MagicNonStreamingCellType = Static<
  typeof MagicNonStreamingCellType
>;
export const MagicStreamingCellType = Union(
  Literal(MagicCellType.MARKDOWN),
  Literal(MagicCellType.CODE),
  Literal(MagicCellType.SQL),
);
export type MagicStreamingCellType = Static<typeof MagicStreamingCellType>;

export const MagicEventSourceLiteral = Union(
  // [PROJECT LOGIC]
  // Any magic action originally from within an individual cell.
  Literal("CELL"),
  // Any magic action originating from within a project, but not an individual cell
  Literal("PROJECT"),
  // Any magic action originating from the "Add Cells" prompt bar in project logic.
  Literal("ADD_CELLS"),
  // Any magic action originating from an empty project's zero state bar.
  Literal("EMPTY_PROJECT"),

  // [EXPLORE]
  // Any magic action originating from within the Explore From Scratch editor.
  Literal("EXPLORE"),
  // Any magic action originating from within the Explore From Here editor.
  Literal("EXPLORE_FROM_HERE"),
  // Any magic action originating from Slack.
  Literal("SLACK"),
  // Any magic action originating from "Ask" (NOT Ask Magic)
  Literal("ASK"),

  // [SEMANTIC AUTHORING]
  // Any magic action originating from within the Semantic Authoring workbench.
  Literal("SEMANTIC_AUTHORING"),

  // Any magic action originating from the Eval / Research team.
  Literal("EVAL"),
  // When magic pauses saying "I can't do that", and the user types in a new prompt
  Literal("UNSUPPORTED_PAUSE"),
  // Any magic action that originates from the home page - this could include "Ask Magic"
  Literal("HOME"),

  // [DEPRECATED]
  Literal("CONTINUE_FROM_CHAIN"), // not used anywhere
  Literal("TR_REGEN"),
);
export type MagicEventSource = Static<typeof MagicEventSourceLiteral>;
export const MagicEventSource = getNormalEnum(MagicEventSourceLiteral);

export const isExploreSource = (source: MagicEventSource | null): boolean =>
  source === "EXPLORE" || source === "EXPLORE_FROM_HERE";

// TODO(MAGIC-REFACTOR): Deprecate this in favor of more accurate/granular MagicKeyword type
export const SmartEditTypeLiteral = Union(
  Literal("FIX_ERRORS"),
  Literal("DOCUMENT"),
  Literal("CUSTOM"),
  Literal("FIX_SPELLING_AND_GRAMMAR"),
  Literal("INSERT"),
  Literal("NAME_CELL"),
  Literal("NAME_CELL_OUTPUT"),
  Literal("DESCRIBE_PROJECT"),
  Literal("NAME_PROJECT"),
  Literal("NAME_PROJECT_FROM_PROMPT"),
  // TODO(izzymiller): Remove after 90 days (if it is after 2025-09-11 you can remove this)
  Literal("SPLIT_CTES"),
);
export type SmartEditType = Static<typeof SmartEditTypeLiteral>;
export const SmartEditType = getNormalEnum(SmartEditTypeLiteral);

export const MagicCellEditWithInputLiteral = Union(
  Literal("CUSTOM"),
  Literal("INSERT"),
  Literal("COMPLETION"),
);
export type MagicCellEditWithInput = Static<
  typeof MagicCellEditWithInputLiteral
>;
export const MagicCellEditWithInput = getNormalEnum(
  MagicCellEditWithInputLiteral,
);

export const MagicCellEditWithoutInputLiteral = Union(
  Literal("FIX_ERRORS"),
  Literal("BASIC_SYNTAX_FIX"),
  Literal("DOCUMENT"),
  Literal("FIX_SPELLING_AND_GRAMMAR"),
);
export type MagicCellEditWithoutInput = Static<
  typeof MagicCellEditWithoutInputLiteral
>;
export const MagicCellEditWithoutInput = getNormalEnum(
  MagicCellEditWithoutInputLiteral,
);

export const MagicCellEditTypeLiteral = Union(
  ...MagicCellEditWithInputLiteral.alternatives,
  ...MagicCellEditWithoutInputLiteral.alternatives,
);
export type MagicCellEditType = Static<typeof MagicCellEditTypeLiteral>;
export const MagicCellEditType = getNormalEnum(MagicCellEditTypeLiteral);

export const MagicCellActionLiteral = Union(
  Literal("NAME_CELL"),
  Literal("NAME_CELL_OUTPUT"),
  Literal("COMPLETION"),
);
export type MagicCellAction = Static<typeof MagicCellActionLiteral>;
export const MagicCellAction = getNormalEnum(MagicCellActionLiteral);
export const MagicProjectActionLiteral = Union(
  Literal("DESCRIBE_PROJECT"),
  Literal("NAME_PROJECT"),
  Literal("INFER_CHAIN_TEMPLATE"),
  Literal("PLAN_CELL_CHAIN_V2"),
);
export type MagicProjectAction = Static<typeof MagicProjectActionLiteral>;
export const MagicProjectAction = getNormalEnum(MagicProjectActionLiteral);
export const MagicSystemActionLiteral = Union(
  Literal("HYDE_PROJECT"),
  Literal("GENERATE_EXAMPLE_PROMPTS"),
);
export type MagicSystemAction = Static<typeof MagicSystemActionLiteral>;
export const MagicSystemAction = getNormalEnum(MagicSystemActionLiteral);

// Magic keywords exclusively associated with the response from
// a prompt template/workflow based run.
export const MagicPromptTemplateKeywordLiteral = Union(
  Literal("EXPERIMENT"),
  Literal("EXPLORE"),
  Literal("SUMMARIZE_CHAIN"),
  Literal("SUMMARIZE_CELL"),
  Literal("INFER_EXPLORE_INTENT"),
  Literal("PLANNER_STEP"),
  ...MagicCellEditTypeLiteral.alternatives,
  ...MagicCellActionLiteral.alternatives,
  ...MagicProjectActionLiteral.alternatives,
  ...MagicSystemActionLiteral.alternatives,
);
export type MagicPromptTemplateKeyword = Static<
  typeof MagicPromptTemplateKeywordLiteral
>;
export const MagicPromptTemplateKeyword = getNormalEnum(
  MagicPromptTemplateKeywordLiteral,
);

// Magic keywords exclusively associated with the response from
// an agentic run.
export const MagicAgentKeywordLiteral = Union(
  Literal("AGENT_RESPONSE"),
  Literal("AGENT_TOOL_CALL"),
  Literal("AGENT_COMPRESSION"),
);
export type MagicAgentKeyword = Static<typeof MagicAgentKeywordLiteral>;
export const MagicAgentKeyword = getNormalEnum(MagicAgentKeywordLiteral);

export const MagicKeywordLiteral = Union(
  ...MagicPromptTemplateKeywordLiteral.alternatives,
  ...MagicAgentKeywordLiteral.alternatives,
);
export type MagicKeyword = Static<typeof MagicKeywordLiteral>;
export const MagicKeyword = getNormalEnum(MagicKeywordLiteral);

// Magic keywords that should not be reflected as a `latestMagicEvent` for a cell
export const ExcludedCellMagicKeywordLiteral = Union(
  Literal(MagicKeyword.NAME_CELL),
  Literal(MagicKeyword.NAME_CELL_OUTPUT),
  Literal(MagicKeyword.SUMMARIZE_CHAIN),
  Literal(MagicKeyword.SUMMARIZE_CELL),
  Literal(MagicKeyword.INFER_EXPLORE_INTENT),
  Literal(MagicKeyword.PLANNER_STEP),
);
export type ExcludedCellMagicKeyword = Static<
  typeof ExcludedCellMagicKeywordLiteral
>;
export const ExcludedCellMagicKeyword = getNormalEnum(
  ExcludedCellMagicKeywordLiteral,
);
export const SystemMagicEventStatusLiteral = Union(
  Literal("CREATED"),
  Literal("PROCESSING"),
  Literal("FAILED"),
  Literal("COMPLETED"),
);
export type SystemMagicEventStatus = Static<
  typeof SystemMagicEventStatusLiteral
>;
export const SystemMagicEventStatus = getNormalEnum(
  SystemMagicEventStatusLiteral,
);

export const SystemMagicEventSourceLiteral = Union(
  Literal("SERVER"),
  Literal("EVAL"),
);
export type SystemMagicEventSource = Static<
  typeof SystemMagicEventSourceLiteral
>;
export const SystemMagicEventSource = getNormalEnum(
  SystemMagicEventSourceLiteral,
);
export const isEventPendingReview = (
  status?: MagicEventStatus | null,
): boolean =>
  status != null &&
  (status === MagicEventStatus.PENDING_REVIEW ||
    status === MagicEventStatus.ERROR_WITH_FALLBACK);

export const MagicEventStatusLiteral = Union(
  Literal("LOADING"),
  Literal("ERROR"),
  // Use this state when we fallback to a safe default, if the LLM generated result cannot be used (some error occurred).
  // For Explore, the fallback is generated at runtime, to maintain the event."result" for debugging and consistent with what the LLM responded with.
  Literal("ERROR_WITH_FALLBACK"),
  // The Magic Event is completed and potentially waiting user action to accept.
  Literal("PENDING_REVIEW"),
  Literal("CANCELLED"),
  // Used when an event is reviewed before applying - useful for Magic Analysis to 'accept' the new cells.
  Literal("REVIEWED"),
  // Indicates that the Magic Event previously failed and was retried. A new Magic Event would be generated.
  Literal("RETRIED"),
);
export type MagicEventStatus = Static<typeof MagicEventStatusLiteral>;
export const MagicEventStatus = getNormalEnum(MagicEventStatusLiteral);

// #region Errors & Warnings

// Special failure events that we show as warnings
// in the UI because a partial result was still achieved.
export const MagicEventWarningLiteral = Union(
  Literal("JOIN_VALIDATION_TIMEOUT"),
);

export const MagicEventFailureLiteral = Union(
  Literal("NO_CONTEXT"),
  Literal("BAD_DATA"),
  Literal("AGENT_ERROR"),
  Literal("AGENT_OVERLOADED"),
  Literal("GENERIC_ERROR"),
  Literal("POSSIBLE_PROMPT_INJECTION"),
  Literal("OVER_LIMIT"),
  Literal("NO_DATA"),
  Literal("INVALID_RESPONSE"),
  Literal("HALLUCINATED_SCHEMA_ELEMENT"),
  Literal("NON_EXPRESSIBLE"),
  Literal("INVALID_JOIN"),
  Literal("EXPLORE_TIMED_OUT"),
  Literal("EXPLORE_ERRORED"),
  Literal("EXPLORE_TABLE_SWAPPED"),
  Literal("UNSUPPORTED_QUERY_ELEMENTS"), // agent wrote SQL for an explore that cannot be expressed in explore UX
  Literal("EXPLORE_NO_DATA"),
  Literal("CHART_ROW_LIMIT_EXCEEDED"),
  Literal("BYOK_AUTHENTICATION"), // Received an authentication error from the provider for a customer's key
  Literal("AGENT_COMPRESSION_ERROR"), // Error occurred while compressing the conversation
  Literal("TOKEN_COUNTING_FAILURE"), // Error occurred while counting tokens
  Literal("REQUEST_CANCELLED"), // Request was cancelled
  Literal("INVALID_MESSAGE_HISTORY"), // Message history is got corrupted
);

export const MagicEventFailureReasonLiteral = Union(
  ...MagicEventFailureLiteral.alternatives,
  ...MagicEventWarningLiteral.alternatives,
);

export type MagicEventFailureReason = Static<
  typeof MagicEventFailureReasonLiteral
>;
export const MagicEventFailureReason = getNormalEnum(
  MagicEventFailureReasonLiteral,
);

export const MagicCellRunResultLiteral = Union(
  Literal("SUCCESS"), // cell ran successfully
  Literal("ERROR"), // cell execution errored
  Literal("TIMEOUT"), // cell took too long to run
  Literal("CANCELLED"), // cell execution was cancelled (e.g. kernel interrupt)
  Literal("NO_DATA"), // sql cell returned no data
  Literal("FAILURE"), // failure that should cause the whole MA to error (e.g. no context)
);
export type MagicCellRunResult = Static<typeof MagicCellRunResultLiteral>;
export const MagicCellRunResult = getNormalEnum(MagicCellRunResultLiteral);

export const TEXT_TOO_LONG_ERROR =
  "AI features only support up to ~3,000 words. Try splitting this cell into multiple cells.";

export const MODEL_OVERLOADED_ERROR =
  "Our model provider is having issues or is at capacity. Try again in a few minutes.";

export const MAGIC_PROVIDER_ERROR =
  "Our model provider is having problems. We will be back as soon as they are!";

export const BYOK_AUTHENTICATION_ERROR =
  "We had a problem authorizing the request with your workspace's API key. Please reach out to an Admin to have them replace the credentials.";

export const GENERIC_MAGIC_ERROR =
  "Something went wrong. Please try again, or contact support if the problem persists.";

export const MAGIC_BAD_PROMPT_ERROR =
  "Your request couldn't be processed. Try rephrasing or providing more specific instructions.";

export const MAGIC_OVER_USAGE_ERROR =
  "You've reached your AI usage limit. Upgrade your plan to continue.";

export const MAGIC_NO_CONTEXT_ERROR =
  "No relevant data was found for your request. Try rephrasing or verifying the data source.";

export const MAGIC_NO_RESULTS =
  "SQL cell returned no data. Review your results before resuming.";

export const MAGIC_BAD_DATA =
  "Unable to create chart with the current data selection.";

export const MAGIC_INVALID_RESPONSE =
  "We couldn't generate a valid response for your request. Try rephrasing or providing more specific instructions.";

export const MAGIC_UNSUPPORTED_QUERY_ELEMENTS =
  "The provided request cannot be expressed in an Exploration.";

const MAGIC_JOIN_VALIDATION_ERROR =
  "There was an issue validating your join. Check column compatibility or try a different join condition.";

const MAGIC_JOIN_VALIDATION_TIMEOUT =
  "We were unable to validate this join. Double check your data's accuracy.";

const MAGIC_NO_SUMMARY_FROM_EXPLORE_TIMEOUT =
  "Your exploration took too long to run. We were unable to generate a summary.";

const MAGIC_GENERATED_EXPLORE_ERRORED =
  "We tried to generate an exploration, but the combination of filters and transformations applied resulted in an error. Try checking and adjusting the column assignments and filter values.";

const MAGIC_SUMMARY_FROM_EXPLORE_TABLE_SWAPPED =
  "We were unable to find relevant data to answer your question. Try picking a different data source.";

const MAGIC_EXPLORE_NO_DATA =
  "Your exploration returned no data. Consider removing a filter or changing your question.";

const MAGIC_EXPLORE_TOO_MANY_ROWS =
  "Your exploration returned too many rows of data. Consider adding a filter or changing your question.";

const MAGIC_AGENT_COMPRESSION_ERROR =
  "The conversation became too long and couldn't be compressed. Please create a new thread.";

const MAGIC_INVALID_MESSAGE_HISTORY =
  "There's an issue with your thread. Consider creating a new thread to continue.";

// This isn't the ideal error message, because we don't actually know what the token counting failure
// implies yet. Maybe it implies the provider is down, but I'm not totally sure. For now, we'll just
// show a generic error and we can update it later.
const MAGIC_TOKEN_COUNTING_FAILURE =
  "Something went wrong. Please try again, or contact support if the problem persists.";

const MAGIC_REQUEST_CANCELLED = "The request was cancelled.";

export const MagicErrorTypeMessageMap: Record<MagicEventFailureReason, string> =
  {
    [MagicEventFailureReason.AGENT_OVERLOADED]: MODEL_OVERLOADED_ERROR,
    [MagicEventFailureReason.GENERIC_ERROR]: GENERIC_MAGIC_ERROR,
    [MagicEventFailureReason.POSSIBLE_PROMPT_INJECTION]: MAGIC_BAD_PROMPT_ERROR,
    [MagicEventFailureReason.AGENT_ERROR]: MAGIC_PROVIDER_ERROR,
    [MagicEventFailureReason.NO_CONTEXT]: MAGIC_NO_CONTEXT_ERROR,
    [MagicEventFailureReason.OVER_LIMIT]: MAGIC_OVER_USAGE_ERROR,
    [MagicEventFailureReason.NO_DATA]: MAGIC_NO_RESULTS,
    [MagicEventFailureReason.BAD_DATA]: MAGIC_BAD_DATA,
    [MagicEventFailureReason.INVALID_RESPONSE]: MAGIC_INVALID_RESPONSE,
    [MagicEventFailureReason.UNSUPPORTED_QUERY_ELEMENTS]:
      MAGIC_UNSUPPORTED_QUERY_ELEMENTS,
    [MagicEventFailureReason.NON_EXPRESSIBLE]: MAGIC_UNSUPPORTED_QUERY_ELEMENTS,
    [MagicEventFailureReason.HALLUCINATED_SCHEMA_ELEMENT]: GENERIC_MAGIC_ERROR, // Should never be shown to the user
    [MagicEventFailureReason.JOIN_VALIDATION_TIMEOUT]:
      MAGIC_JOIN_VALIDATION_TIMEOUT,
    [MagicEventFailureReason.INVALID_JOIN]: MAGIC_JOIN_VALIDATION_ERROR,
    [MagicEventFailureReason.EXPLORE_TIMED_OUT]:
      MAGIC_NO_SUMMARY_FROM_EXPLORE_TIMEOUT,
    [MagicEventFailureReason.EXPLORE_ERRORED]: MAGIC_GENERATED_EXPLORE_ERRORED,
    [MagicEventFailureReason.EXPLORE_TABLE_SWAPPED]:
      MAGIC_SUMMARY_FROM_EXPLORE_TABLE_SWAPPED,
    [MagicEventFailureReason.EXPLORE_NO_DATA]: MAGIC_EXPLORE_NO_DATA,
    [MagicEventFailureReason.CHART_ROW_LIMIT_EXCEEDED]:
      MAGIC_EXPLORE_TOO_MANY_ROWS,
    [MagicEventFailureReason.BYOK_AUTHENTICATION]: BYOK_AUTHENTICATION_ERROR,
    [MagicEventFailureReason.AGENT_COMPRESSION_ERROR]:
      MAGIC_AGENT_COMPRESSION_ERROR,
    [MagicEventFailureReason.TOKEN_COUNTING_FAILURE]:
      MAGIC_TOKEN_COUNTING_FAILURE,
    [MagicEventFailureReason.REQUEST_CANCELLED]: MAGIC_REQUEST_CANCELLED,
    [MagicEventFailureReason.INVALID_MESSAGE_HISTORY]:
      MAGIC_INVALID_MESSAGE_HISTORY,
  };

/**
 * When the Explore errors, determine if the error type should permit Magic exposing the Hex URL to the user.
 * This is a "best guess" effort so that Slack can include links when relevant.
 */
export const ShowExploreOnError: Record<MagicEventFailureReason, boolean> = {
  // Explore errors that would still allow a user to view the Explore link.
  [MagicEventFailureReason.INVALID_JOIN]: true,
  [MagicEventFailureReason.EXPLORE_TIMED_OUT]: true,
  [MagicEventFailureReason.EXPLORE_ERRORED]: true,
  [MagicEventFailureReason.EXPLORE_NO_DATA]: true,
  [MagicEventFailureReason.CHART_ROW_LIMIT_EXCEEDED]: true,
  [MagicEventFailureReason.NO_DATA]: true,
  [MagicEventFailureReason.AGENT_COMPRESSION_ERROR]: true,

  // TODO: separate between hallucinated schema elements that can be removed, and ones that cannot be removed.
  // If the base table is hallucinated, then we can't show the Explore, but if only a filter or column is hallucinated,
  // we should be able to still show the Explore.
  [MagicEventFailureReason.HALLUCINATED_SCHEMA_ELEMENT]: false,

  [MagicEventFailureReason.GENERIC_ERROR]: false,
  [MagicEventFailureReason.EXPLORE_TABLE_SWAPPED]: false,
  [MagicEventFailureReason.JOIN_VALIDATION_TIMEOUT]: false,
  [MagicEventFailureReason.NO_CONTEXT]: false,
  [MagicEventFailureReason.BAD_DATA]: false,
  [MagicEventFailureReason.AGENT_ERROR]: false,
  [MagicEventFailureReason.AGENT_OVERLOADED]: false,
  [MagicEventFailureReason.POSSIBLE_PROMPT_INJECTION]: false,
  [MagicEventFailureReason.OVER_LIMIT]: false,
  [MagicEventFailureReason.INVALID_RESPONSE]: false,
  [MagicEventFailureReason.NON_EXPRESSIBLE]: false,
  [MagicEventFailureReason.BYOK_AUTHENTICATION]: false,
  [MagicEventFailureReason.UNSUPPORTED_QUERY_ELEMENTS]: false,
  [MagicEventFailureReason.TOKEN_COUNTING_FAILURE]: false,
  [MagicEventFailureReason.REQUEST_CANCELLED]: false,
  [MagicEventFailureReason.INVALID_MESSAGE_HISTORY]: false,
};

// #endregion Errors & Warnings

export const MagicTableReference = RRecord({
  db: String.Or(Null),
  schema: String,
  table: String,
});
export type MagicTableReference = Static<typeof MagicTableReference>;

export const MagicExploreTableInfo = RRecord({
  baseTable: MagicTableReference,
  joinTables: RArray(MagicTableReference),
});
export type MagicExploreTableInfo = Static<typeof MagicExploreTableInfo>;

export const MagicHydeQuestion = String;
export type MagicHydeQuestion = Static<typeof MagicHydeQuestion>;

// NB - this is part of the function payload sent to the LLM; use caution if modifying,
// as it could affect response quality
export const MentionedDataframeNameSchema = RArray(
  RRecord({ name: String, type: String }),
);
export type MentionedDataframeNameSchema = Static<
  typeof MentionedDataframeNameSchema
>;

export const VariableSpec = RRecord({
  name: String,
  type: String,
  dataframeSchema: Optional(MentionedDataframeNameSchema),
});
export type VariableSpec = Static<typeof VariableSpec>;
export type VariableMap = Record<string, VariableSpec>;

export const MagicPromptGenerationCellSpec = RRecord({
  type: MagicCellTypeLiteral,
  cellTitle: String,
  inputVariables: RArray(VariableSpec),
  outputVariables: RArray(VariableSpec),
  prompt: String,
});

export type MagicPromptGenerationCellSpec = Static<
  typeof MagicPromptGenerationCellSpec
>;

export const HexMagicApiKeySentinel = "HEX_DEFAULT" as const;
export type HexMagicApiKeySentinel = typeof HexMagicApiKeySentinel;

export const MagicEventMagicApiKey = Union(
  Literal(HexMagicApiKeySentinel),
  MagicApiKeyId,
);
export type MagicEventMagicApiKey = Static<typeof MagicEventMagicApiKey>;

export const RealMagicProviderTypeLiteral = Union(
  Literal("OPENAI"),
  Literal("ANTHROPIC"),
  Literal("BASETEN"),
  Literal("SNOWFLAKE_CORTEX"),
);
export type RealMagicProviderType = Static<typeof RealMagicProviderTypeLiteral>;
export const RealMagicProviderType = getNormalEnum(
  RealMagicProviderTypeLiteral,
);

export const MagicProviderTypeLiteral = Union(
  ...RealMagicProviderTypeLiteral.alternatives,
  Literal("MOCK"),
);
export type MagicProviderType = Static<typeof MagicProviderTypeLiteral>;
export const MagicProviderType = getNormalEnum(MagicProviderTypeLiteral);

export const StreamingKey = Union(
  RRecord({
    key: String,
    type: Literal("STRING"),
  }),
  RRecord({ key: RArray(String), type: Literal("MULTI_OBJECT") }),
  RRecord({
    key: String,
    type: Literal("OBJECT_ARRAY"),
  }),
);
export type StreamingKey = Static<typeof StreamingKey>;

export const MockToolCalls = RArray(
  RRecord({
    arguments: RRecord({}),
    name: String,
  }).And(
    Partial({
      thinking: Optional(RArray(String)),
    }),
  ),
);
export const MockFunction = RRecord({
  streamingKey: Optional(StreamingKey),
  function: Dictionary(Unknown, String),
});
const StringOrFunction = String.Or(MockFunction);
export const StringOrFunctionOrFailureType = Union(
  StringOrFunction,
  MagicEventFailureReasonLiteral,
);
export type StringOrFunctionOrFailureType = Static<
  typeof StringOrFunctionOrFailureType
>;
export const MockResponse = Union(
  StringOrFunction,
  MagicEventFailureReasonLiteral,
  MockToolCalls,
);
export type MockResponse = Static<typeof MockResponse>;
export const MockProviderModelParams = RRecord({
  model: Union(Literal("MOCK_PRIMARY_MODEL"), Literal("MOCK_FALLBACK_MODEL")),
  sleepTime: Optional(Number),
  /**
   * Map prompt template type to a response. Use for
   * one-shot prompts.
   */
  responseMap: Dictionary(
    Optional(
      Union(
        StringOrFunctionOrFailureType,
        Dictionary(StringOrFunctionOrFailureType, String),
      ),
    ),
    PromptTemplateTypeLiteral,
  ),
}).And(
  Partial({
    /**
     * Response queue, agent will make these calls in order.
     * Use for agents.
     */
    responseQueue: RArray(MockResponse),
  }),
);

export type MockProviderModelParams = Static<typeof MockProviderModelParams>;

export const MockProviderConfig = RRecord({
  agent: Literal(MagicProviderType.MOCK),
  params: MockProviderModelParams,
});
export type MockProviderConfig = Static<typeof MockProviderConfig>;

export const OpenAiProviderConfig = RRecord({
  agent: Literal(MagicProviderType.OPENAI),
  params: OpenAiModelParams,
});
export type OpenAiProviderConfig = Static<typeof OpenAiProviderConfig>;

export const AnthropicProviderConfig = RRecord({
  agent: Literal(MagicProviderType.ANTHROPIC),
  params: AnthropicModelParams,
});
export type AnthropicProviderConfig = Static<typeof AnthropicProviderConfig>;

export const BasetenLLMModelConfig = RRecord({
  modelKeys: RRecord({
    us: String,
    eu: String,
    dev: String,
  }),
  maxInputTokens: Number,
  maxOutputTokens: Optional(Number), // Not used by reasoning models
});
export type BasetenLLMModelConfig = Static<typeof BasetenLLMModelConfig>;

// Should approximately match the OpenAI model params,
// but with a different model type
export const BasetenLLMModelParams = RRecord({
  frequencyPenalty: Number,
  n: Number,
  presencePenalty: Number,
  stopWords: RArray(String),
  temperature: Optional(Number), // Not used by reasoning models
  topP: Number,
  reasoningEffort: Optional(
    Union(Literal("low"), Literal("medium"), Literal("high")),
  ), // ONLY used by reasoning models
  model: BasetenLLMModelConfig,
});
export type BasetenLLMModelParams = Static<typeof BasetenLLMModelParams>;

export const BasetenProviderConfig = RRecord({
  agent: Literal(MagicProviderType.BASETEN),
  params: BasetenLLMModelParams,
});
export type BasetenProviderConfig = Static<typeof BasetenProviderConfig>;

export const SnowflakeCortexProviderConfig = RRecord({
  agent: Literal(MagicProviderType.SNOWFLAKE_CORTEX),
  params: SnowflakeCortexModelParams,
});
export type SnowflakeCortexProviderConfig = Static<
  typeof SnowflakeCortexProviderConfig
>;

export const MagicProviderConfig = Union(
  OpenAiProviderConfig,
  AnthropicProviderConfig,
  BasetenProviderConfig,
  MockProviderConfig,
  SnowflakeCortexProviderConfig,
);
export type MagicProviderConfig = Static<typeof MagicProviderConfig>;

export const ModelProviderStaticConfigNameLiteral = Union(
  Literal("OPENAI_GPT_41"),
  Literal("OPENAI_GPT_41_REASONING"),
  Literal("OPENAI_GPT_41_NANO"),
  Literal("OPENAI_GPT_4_OMNI_MINI"),
  Literal("OPENAI_GPT_4_OMNI"),
  Literal("OPENAI_GPT_5_REASONING"),
  Literal("OPENAI_GPT_51"),
  Literal("OPENAI_GPT_51_LOW_REASONING"),
  Literal("OPENAI_GPT_51_REASONING"),
  Literal("OPENAI_GPT_51_HIGH_REASONING"),
  Literal("OPENAI_GPT_52"),
  Literal("OPENAI_GPT_52_LOW_REASONING"),
  Literal("OPENAI_GPT_52_LOW_REASONING_LOW_VERBOSITY"),
  Literal("OPENAI_GPT_52_REASONING"),
  Literal("OPENAI_GPT_52_HIGH_REASONING"),
  Literal("ANTHROPIC_CLAUDE_4_SONNET"),
  Literal("ANTHROPIC_CLAUDE_4_SONNET_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_SONNET_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_SONNET_SMALL_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_SONNET_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_HAIKU"),
  Literal("ANTHROPIC_CLAUDE_4_5_HAIKU_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_OPUS_NO_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_OPUS_SMALL_THINKING"),
  Literal("ANTHROPIC_CLAUDE_4_5_OPUS_THINKING"),
  Literal("BASETEN_DEFAULT_EXPLORE_EXECUTOR"),
  Literal("BASETEN_DEFAULT_TYPEAHEAD"),
  Literal("CORTEX_CLAUDE_4_SONNET"),
  Literal("CORTEX_CLAUDE_4_SONNET_THINKING"),
  Literal("CORTEX_CLAUDE_4_5_SONNET"),
  Literal("CORTEX_CLAUDE_4_5_SONNET_THINKING"),
);
export type ModelProviderStaticConfigName = Static<
  typeof ModelProviderStaticConfigNameLiteral
>;
export const ModelProviderStaticConfigName = getNormalEnum(
  ModelProviderStaticConfigNameLiteral,
);

export const ModelFeatureKeyLiteral = Union(
  Literal("logic-view-agent"),
  Literal("ask-agent"),
  Literal("ask-agent-no-thinking"),
  Literal("ask-agent-small-thinking"),
  Literal("semantic-authoring-agent"),
  Literal("data-discovery-agent"),
  Literal("default-one-shot"),
  Literal("magic-analysis"),
  Literal("fix-code-basic"),
  Literal("generate-chart"),
  Literal("online-eval-task-completion"),
  Literal("thread-summarization"),
  Literal("confusion-detection"),
  Literal("explore-planner"),
  Literal("hex-content-generation"),
  Literal("explore-executor"),
  Literal("typeahead"),
  Literal("generate-suggested-prompts-sync"),
  Literal("app-agent"),
  Literal("thread-topic-classification"),
  // Unused / dormant
  // Replace with ask-agent soon.
  Literal("explore-agent"),
  // Used for testing
  Literal("mock-agent"),
);

export type ModelFeatureKey = Static<typeof ModelFeatureKeyLiteral>;
export const ModelFeatureKey = getNormalEnum(ModelFeatureKeyLiteral);

export const ModelFeatureConfig = RRecord({
  features: Dictionary(
    RArray(
      // We have to union String here to allow deploying new static configs
      // without breaking the validation in RC and prod.
      Union(MagicProviderConfig, ModelProviderStaticConfigNameLiteral, String),
    ),
    String,
  ),
});
/**
 * Manually narrow the compile-time type to be more specific and only allow
 * keys in the features object to be ModelFeatureKey's. This is necessary
 * because Runtypes would fail to validate nested extra keys in a dictionary
 * defined based on a literal string union, so we broaden the runtype to allow
 * any string key, and then manually narrow the typescript type to ensure
 * compile-time compliance.
 */
export type ModelFeatureConfig = Static<typeof ModelFeatureConfig> & {
  features: Record<
    ModelFeatureKey,
    Array<MagicProviderConfig | ModelProviderStaticConfigName>
  > &
    // Add a catch-all for unknown keys to ensure this type is as correct as possible
    Record<string, unknown>;
};

export const MagicConfig = RRecord({
  primaryConfig: MagicProviderConfig,
});
export type MagicConfig = Static<typeof MagicConfig>;
export const OpenAiApiConfig = RRecord({
  organization: String.optional(),
  apiKey: String,
  baseURL: String.optional(),
});
export type OpenAiApiConfig = Static<typeof OpenAiApiConfig>;
export const MagicApiConfig = Union(OpenAiApiConfig);
export type MagicApiConfig = Static<typeof MagicApiConfig>;

export const MagicTypeAheadProviderConfig = RRecord({
  provider: MagicTypeAheadProviderLiteral,
  config: RRecord({}),
});
export type MagicTypeAheadProviderConfig = Static<
  typeof MagicTypeAheadProviderConfig
>;

export const MagicAcceptTypeLiteral = Union(
  Literal("KEEP"),
  Literal("KEEP_AND_RUN"),
  Literal("KEEP_IN_NEW_CELL"),
  Literal("KEEP_AND_RUN_IN_NEW_CELL"),
  Literal("KEEP_AND_EDIT"),
  Literal("REJECT"),
);
export type MagicAcceptType = Static<typeof MagicAcceptTypeLiteral>;
export const MagicAcceptType = getNormalEnum(MagicAcceptTypeLiteral);

export const MagicCancelReasonLiteral = Union(
  Literal("MANUAL"),
  Literal("GEN_CODE_ERROR"),
  Literal("TIMEOUT"),
  Literal("NO_DATA"),
  Literal("UNSUPPORTED"),
  Literal("DEFLECTION"),
);
export type MagicCancelReason = Static<typeof MagicCancelReasonLiteral>;
export const MagicCancelReason = getNormalEnum(MagicCancelReasonLiteral);

export const MagicFeedbackTypeLiteral = Union(
  Literal("GENERATION"),
  Literal("DEFLECTION"),
);
export type MagicFeedbackType = Static<typeof MagicFeedbackTypeLiteral>;
export const MagicFeedbackType = getNormalEnum(MagicFeedbackTypeLiteral);

export const ColumnReference = RRecord({
  name: String,
  type: String,
  description: String.Or(Null),
  dataManagerDescription: Optional(String),
  vectorDistance: Optional(Number),
});
export type ColumnReference = Static<typeof ColumnReference>;

// TODO: Seems bad to just duplicate this definition, but it won't import correctly from hex_sl_schema...
export const MeasureFilter = RRecord({
  sql: Optional(String.Or(Null)),
  calc: Optional(Union(String, RRecord({})).Or(Null)),
});
export type MeasureFilter = Static<typeof MeasureFilter>;

export const SemanticDatasetColumnReference = ColumnReference.extend({
  semanticType: Literal("MEASURE").Or(Literal("DIMENSION")),
  filters: Optional(RArray(MeasureFilter)),
});
export type SemanticDatasetColumnReference = Static<
  typeof SemanticDatasetColumnReference
>;

export const MagicDataframeReferenceTypeLiteral = Union(
  Literal("DATAFRAME"),
  Literal("SNOWPARK_DATAFRAME"),
  Literal("REMOTE_DATAFRAME"),
  Literal("BIGFRAMES_DATAFRAME"),
);
export const MagicDataframeReferenceType = getNormalEnum(
  MagicDataframeReferenceTypeLiteral,
);

export const MagicTableReferenceTypeLiteral = Literal("TABLE");
export const MagicSemanticDatasetReferenceTypeLiteral =
  Literal("SEMANTIC_DATASET");

export const MagicReferenceTypeLiteral = Union(
  ...MagicDataframeReferenceTypeLiteral.alternatives,
  MagicSemanticDatasetReferenceTypeLiteral,
  MagicTableReferenceTypeLiteral,
);
export const MagicReferenceType = getNormalEnum(MagicReferenceTypeLiteral);
export type MagicReferenceType = Static<typeof MagicReferenceTypeLiteral>;

export const ReferenceInfo = RRecord({
  dataConnectionId: Optional(DataConnectionId),
  table: String,
  type: MagicReferenceTypeLiteral,
  tableId: Optional(DataSourceTableId),
  tableDescription: String.Or(Null),
  schema: String.Or(Null),
  schemaId: Optional(DataSourceSchemaId),
  database: String.Or(Null),
  databaseId: Optional(DataSourceDatabaseId),
  columns: RArray(ColumnReference),
  dataManagerDescription: Optional(String),
  tablePrioritized: Boolean.Or(Null),
  schemaPrioritized: Boolean.Or(Null),
  databasePrioritized: Boolean.Or(Null),
  formatted: Optional(String),
});

export type ReferenceInfo = Static<typeof ReferenceInfo>;

export const SemanticDatasetReferenceInfo = ReferenceInfo.extend({
  database: Null,
  schema: Literal("_hex"),
  type: Literal("SEMANTIC_DATASET"),
  timeUnits: RArray(String),
  columns: RArray(SemanticDatasetColumnReference),
  joins: RArray(
    RRecord({
      name: String.Or(Null),
      target: String,
    }),
  ),
  validQueryPaths: RArray(RArray(String)),
});

export type SemanticDatasetReferenceInfo = Static<
  typeof SemanticDatasetReferenceInfo
>;
export const MagicClientReferenceInfo = Union(
  RRecord({
    id: DataSourceTableId,
    type: MagicTableReferenceTypeLiteral,
    name: String,
  }),
  RRecord({
    name: String,
    type: MagicDataframeReferenceTypeLiteral,
  }),
);

export type MagicClientReferenceInfo = Static<typeof MagicClientReferenceInfo>;

export const CELL_TYPE_BY_CHAIN_TEMPLATE_TYPE: Record<
  MagicCellChainTemplateType,
  MagicCellType[]
> = {
  [MagicCellChainTemplateType.CODE]: [MagicCellType.CODE],
  [MagicCellChainTemplateType.CHART]: [MagicCellType.CHART],
  [MagicCellChainTemplateType.SQL]: [MagicCellType.SQL],
};

export const GENERATION_PROMPT_TEMPLATE_BY_CELL_TYPE = {
  [MagicCellType.SQL]: PromptTemplateType.GENERATE_SQL,
  [MagicCellType.CODE]: PromptTemplateType.GENERATE_PYTHON,
  [MagicCellType.CHART]: PromptTemplateType.GENERATE_CHART,
};

export const CELL_TYPE_BY_PROMPT_TEMPLATE = {
  [PromptTemplateType.GENERATE_SQL]: MagicCellType.SQL,
  [PromptTemplateType.GENERATE_PYTHON]: MagicCellType.CODE,
  [PromptTemplateType.GENERATE_CHART]: MagicCellType.CHART,
  [PromptTemplateType.SIMPLE_CHART]: MagicCellType.CHART,
};

// GeneratePromptComponentsOptions is used to generate the project context for a Magic Event
export const GeneratePromptComponentsOptions = RRecord({
  previousPromptIds: RArray(MagicEventId), // Ids of previous prompts in the project
  existingChainCellIds: RArray(CellId), // Ids of existing cells in the current chain to be generated
  traceback: String.Or(Null), // Traceback of the current cell
  contentToEdit: String.Or(Null), // alternative text/code to edit (i.e. not the existing cell source); used for e.g. diff-based-editing autofixes
  agentGeneratedPrompt: String.Or(Null), // A prompt meant for an LLM that was generated by a previous call to the LLM (e.g., the 'Plan' in PlanExec).
  prevErrorDescription: String.Or(Null), // A description of an error the model previously made
});

export type GeneratePromptComponentsOptions = Static<
  typeof GeneratePromptComponentsOptions
>;

export const MagicCellContentsReferences = RRecord({
  tableReferences: RArray(String),
});
export type MagicCellContentsReferences = Static<
  typeof MagicCellContentsReferences
>;

export const MagicLiteralType = Union(
  Literal("STRING"),
  Literal("FLOAT"),
  Literal("INTEGER"),
  Literal("BOOLEAN"),
  Literal("DATETIME"),
);
export type MagicLiteralType = Static<typeof MagicLiteralType>;

export const MagicExploreColumnRef = RRecord({
  column: String,
  db: String.Or(Null),
  // schema and table should never be null for new explore generations, but are nullable for
  // backcompat with previously generated suggested prompts
  schema: String.Or(Null),
  table: String.Or(Null),
});
export type MagicExploreColumnRef = Static<typeof MagicExploreColumnRef>;

export const MagicExploreStubField = RRecord({
  columns: RArray(MagicExploreColumnRef), // Array of column names that this field depends on
  alias: String.Or(Null), // Alias for the field, if any
  identifier: String, // The raw identifier for the field (either a calc expression, column name, literal, or "*")
  type: MagicLiteralType.Or(Null), // The type of the field, if it's a literal
  agg: HqlAggregationFunctionLiteral.Or(Null), // The aggregation to apply to the field in the explore spec
  calc: Boolean, // Whether the field is a calculated field
});

export type MagicExploreStubField = Static<typeof MagicExploreStubField>;

export const UnconsumedQueryElementCategoryLiteral = Union(
  Literal("JOIN"),
  Literal("FILTER"),
  Literal("SELECT"),
  Literal("ORDER"),
  Literal("ROOT"),
  Literal("WINDOW"),
);
export type UnconsumedQueryElementCategory = Static<
  typeof UnconsumedQueryElementCategoryLiteral
>;
export const UnconsumedQueryElementCategory = getNormalEnum(
  UnconsumedQueryElementCategoryLiteral,
);

export const MagicExploreRelativeDateField = RRecord({
  amount: Number,
  unit: RelativeDateUnitLiteral,
  operation: RelativeDateOperationLiteral,
  column: MagicExploreColumnRef,
  calc: Literal(false),
});
export type MagicExploreRelativeDateField = Static<
  typeof MagicExploreRelativeDateField
>;

export const UnconsumedQueryElements = Dictionary(
  RArray(String),
  UnconsumedQueryElementCategoryLiteral,
);
export type UnconsumedQueryElements = Static<typeof UnconsumedQueryElements>;

export const MagicExploreStubFilterPredicate = Union(
  ListBinaryColumnPredicateOpLiteral,
  BinaryColumnPredicateOpLiteral,
  UnaryColumnPredicateOpLiteral,
);

export const MagicExploreSqlParseOrderBy = RRecord({
  by: RArray(MagicExploreStubField),
  desc: Boolean,
}).Or(Null);

export type MagicExploreSqlParseOrderBy = Static<
  typeof MagicExploreSqlParseOrderBy
>;

export const MagicExploreStubFilters = RArray(
  RRecord({
    predicate: MagicExploreStubFilterPredicate,
    a: Union(MagicExploreStubField, MagicExploreRelativeDateField),
    b: Union(RArray(MagicExploreStubField), MagicExploreStubField, Null),
  }),
);
export type MagicExploreStubFilters = Static<typeof MagicExploreStubFilters>;

export const MagicExploreStubJoins = RArray(
  RRecord({
    baseTable: MagicTableReference,
    table: MagicTableReference,
    on: RRecord({
      a: MagicExploreStubField,
      b: MagicExploreStubField,
    }),
  }),
).Or(Null);

export type MagicExploreStubJoins = Static<typeof MagicExploreStubJoins>;

export const MagicExploreSqlParse = RRecord({
  // All fields selected in the query
  fields: RArray(MagicExploreStubField),
  // Unconsumed query elements, grouped by category If non-empty, indicates the agent response is
  // not expressible in the explore UX
  unconsumedQueryElements: UnconsumedQueryElements,
  // Array of AND-ed filters in the query. Because the explore UX does not support OR filters,
  // those are represented as calculated fields + a simple IS_TRUE filter on the calculated column.
  filters: MagicExploreStubFilters,
  // The table the agent selected from in the query
  table: MagicTableReference,
  // The limit clause in the query --> Not currently used
  limit: Number.Or(Null),
  // Specification of the fields to order by in the query
  order: MagicExploreSqlParseOrderBy,
  joins: MagicExploreStubJoins,
});
export type MagicExploreSqlParse = Static<typeof MagicExploreSqlParse>;

export const UserMagicConfig = RRecord({
  overMagicUsageLimit: Boolean,
  magicEnabled: Boolean,
  magicTypeaheadAllowed: Boolean,
  onboardedToMagic: Boolean,
  onboardedToMagicTypeahead: Boolean,
});
export type UserMagicConfig = Static<typeof UserMagicConfig>;

// ProjectContextForPromptGeneration is the context used to generate Prompt Components for a Magic Event
export const ProjectContextForPromptGeneration = RRecord({
  previousPrompts: RArray(
    RRecord({ prompt: String, referencedTables: RArray(MagicTableReference) }),
  ), // Previous prompts in the project
  existingChainCells: RArray(MagicPromptGenerationCellSpec), // Existing cells in the current chain to be generated
  projectCategories: RArray(
    RRecord({ name: String, description: String.Or(Null) }),
  ), // Categories of the project
  projectCellSources: RArray(
    RRecord({
      contents: String,
      title: String,
    }),
  ), // Contents and title of each cell in the project
  projectTitle: String, // Title of the project
  projectVariables: RArray(VariableName), // Existing in-scope variables for the project that the agent should not overwrite in its generated code
  projectImports: RArray(String), // Existing imports in the project that the agent should not include in its generated code
  previousEdits: RArray(
    RRecord({
      prompt: String,
      result: String,
    }),
  ),
  previousExploreEdits: RArray(
    RRecord({
      id: MagicEventId,
      prompt: String,
      result: String.Or(Null),
      functionArguments: String.Or(Null),
      type: MagicKeywordLiteral,
    }),
  ),
  projectStatus: RRecord({
    name: String,
    description: String.Or(Null),
  }),
  magicRules: String.Or(Null),
  chainContents: RArray(
    RRecord({ cellType: MagicCellTypeLiteral, contents: String }),
  ),
  lastCellOutput: DisplayTableOutput,
  targetCellContents: String,
  exploreImage: RRecord({
    base64Content: String,
    mimeType: MagicSupportedImageMimeTypes,
  }),
  exploreOutputs: RRecord({
    table: Optional(DisplayTableOutput),
    rowCount: Optional(Number),
    pageSize: Optional(Number),
  }),
  exploreConfig: RRecord({
    sqlQuery: Optional(String), // Raw SQL query string the model generated.
    sqlParse: Optional(MagicExploreSqlParse), // Query string parsed to "magic stub" schema.
  }),
  previousError: RRecord({
    description: String, // A description of the failure that's causing us to retry the event.
    result: String, // The result of the previous event.
  }).Or(Null),
  relatedCellSources: RArray(
    RRecord({
      contents: String,
      language: String,
      title: String,
    }),
  ), // Contents and metadata of related cells for context
});

export type ProjectContextForPromptGeneration = Static<
  typeof ProjectContextForPromptGeneration
>;

// Magic API Key types

// These are fields we should mask from being returned to a user
export const SecureField = Union(
  Literal("apiKey"),
  Literal("privateKey"),
  Literal("privateKeyPassphrase"),
);
export type SecureField = Static<typeof SecureField>;
export const SecureFieldsArray: SecureField[] = [
  "apiKey",
  "privateKey",
  "privateKeyPassphrase",
];

// Optionally stored to override the region-specific base URL for the stack.
// Needs to match an "https://*.openai.com" pattern, without the /v1 suffix.
export const OpenAIBaseURL = String.withConstraint(
  (s) =>
    /^https:\/\/[a-zA-Z0-9.-]+\.openai\.com\/?$/.test(s) ||
    "Must be an https://<region>.openai.com URL",
);
export type OpenAIBaseURL = Static<typeof OpenAIBaseURL>;

export const OpenAIMagicApiKeyDetails = RRecord({
  apiKey: String,
  openAIOrgId: String,
  baseURL: OpenAIBaseURL.optional(),
});

export type OpenAIMagicApiKeyDetails = Static<typeof OpenAIMagicApiKeyDetails>;

export const AnthropicMagicApiKeyDetails = RRecord({
  apiKey: String,
});

export type AnthropicMagicApiKeyDetails = Static<
  typeof AnthropicMagicApiKeyDetails
>;

export const SnowflakeCortexMagicApiKeyDetails = RRecord({
  snowflakeAccountId: String,
  snowflakeUsername: String,
  privateKey: String,
  privateKeyPassphrase: String.optional(),
});

export type SnowflakeCortexMagicApiKeyDetails = Static<
  typeof SnowflakeCortexMagicApiKeyDetails
>;

export const MagicApiKeyDetails = Union(
  RRecord({
    providerName: Literal(MagicProviderType.OPENAI),
    details: OpenAIMagicApiKeyDetails,
  }),
  RRecord({
    providerName: Literal(MagicProviderType.ANTHROPIC),
    details: AnthropicMagicApiKeyDetails,
  }),
  RRecord({
    providerName: Literal(MagicProviderType.SNOWFLAKE_CORTEX),
    details: SnowflakeCortexMagicApiKeyDetails,
  }),
);
export type MagicApiKeyDetails = Static<typeof MagicApiKeyDetails>;

// Invalid key specification returned from validation failure
export const InvalidMagicApiKey = RRecord({
  providerName: Union(
    Literal(MagicProviderType.OPENAI),
    Literal(MagicProviderType.ANTHROPIC),
    Literal(MagicProviderType.SNOWFLAKE_CORTEX),
  ),
  message: String,
});
export type InvalidMagicApiKey = Static<typeof InvalidMagicApiKey>;

// The types of "dataframe" sources that an explore cell can have.
// This is used by magic to determine how different types of tables/columns/etc
// should be loaded.
export type ExploreSourceType =
  | "DATA_WAREHOUSE"
  | "DATAFRAME"
  | "SEMANTIC_DATASET";
